import React, { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, MousePointerClick, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HeatmapPanel({ token, days, pages = [] }) {
  const [path, setPath] = useState("/");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [frameH, setFrameH] = useState(2600);
  const [showSite, setShowSite] = useState(true);
  const iframeRef = useRef(null);

  const fetchHeat = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/api/admin/heatmap?path=${encodeURIComponent(path)}&days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(await r.json());
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [path, days, token]);

  useEffect(() => { fetchHeat(); }, [fetchHeat]);

  const onLoadFrame = () => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc) {
        const h = Math.max(
          doc.body ? doc.body.scrollHeight : 0,
          doc.documentElement ? doc.documentElement.scrollHeight : 0
        );
        if (h > 100) setFrameH(h);
      }
    } catch (e) {
      // cross-origin guard (shouldn't happen, same origin)
    }
  };

  const points = data?.points || [];
  const pathOptions = Array.from(
    new Set(["/", ...(data?.paths || []), ...(pages || []).map((p) => p.path)])
  );

  return (
    <div className="space-y-4" data-testid="heatmap-view">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2 text-slate-700">
            <MousePointerClick className="w-4 h-4 text-[#D32F2F]" />
            <h2 className="text-sm font-semibold">Click Heatmap</h2>
            <span className="text-xs text-slate-400">
              {data?.count ?? 0} clicks · last {days}D
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="heatmap-toggle-site"
              onClick={() => setShowSite((s) => !s)}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-1.5 transition-colors"
            >
              {showSite ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showSite ? "Hide page" : "Show page"}
            </button>
            <select
              value={path}
              onChange={(e) => setPath(e.target.value)}
              data-testid="heatmap-path-select"
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700"
            >
              {pathOptions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>

        <p className="text-xs text-slate-400 mb-4">
          Warmer / denser red areas show where visitors click most on this page. Data is collected from real visitors.
        </p>

        {loading && (
          <div className="h-40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#D32F2F]" />
          </div>
        )}

        {!loading && (
          <div
            className="relative mx-auto overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
            style={{ maxWidth: 1280 }}
            data-testid="heatmap-canvas"
          >
            <iframe
              ref={iframeRef}
              title="heatmap-site-preview"
              src={`${window.location.origin}${path}?heatmap=1`}
              onLoad={onLoadFrame}
              scrolling="no"
              className="w-full block pointer-events-none"
              style={{ height: frameH, border: 0, opacity: showSite ? 1 : 0 }}
            />
            <div className="absolute inset-0 pointer-events-none">
              {points.map((pt, i) => (
                <span
                  key={i}
                  style={{
                    position: "absolute",
                    left: `${pt.x * 100}%`,
                    top: `${pt.y * 100}%`,
                    width: 44,
                    height: 44,
                    transform: "translate(-50%, -50%)",
                    borderRadius: "9999px",
                    background:
                      "radial-gradient(circle, rgba(211,47,47,0.5) 0%, rgba(211,47,47,0.18) 45%, rgba(211,47,47,0) 72%)",
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {!loading && points.length === 0 && (
          <p className="text-sm text-slate-400 text-center mt-4">
            No click data yet for this page. Visit the site and click around to generate data.
          </p>
        )}
      </div>
    </div>
  );
}
